import { formatDate } from './formatters.js';

/**
 * 에너지원 코드를 한글 이름으로 변환
 * @param {string} code - 에너지원 코드
 * @param {Array} energySourceCode - 에너지원 코드 배열 (vuex state.commonCodes.energySourceCode)
 * @returns {string} 에너지원 한글 이름
 */
export function getEnergySourceName(code, energySourceCode = []) {
    if (!code || !Array.isArray(energySourceCode)) {
        return code || "-";
    }

    const energySource = energySourceCode.find((item) => item.value === code);
    return energySource ? energySource.text : code;
}

/**
 * 상태 코드를 한글 이름으로 변환
 * @param {string} status - 상태 코드 (active, pending, predict)
 * @returns {string} 상태 한글 이름
 */
export function getStatusName(status) {
    const map = {
        active: "요금 계산",
        pending: "계산 대기",
        predict: "요금 예측",
    };
    return map[status] || "-";
}

/**
 * 시간대 코드를 한글 이름으로 변환
 * @param {string} slot - 시간대 코드 (LOW, MID, PEAK)
 * @returns {string} 시간대 한글 이름
 */
export function getTimeSlotName(slot) {
    const map = {
        LOW: "경부하",
        MID: "중간부하",
        PEAK: "최대부하",
    };
    return map[slot] || slot;
}

/**
 * 비교 차이값에 따라 CSS 클래스 반환
 * @param {number} diff - 비교 차이값 (양수: 증가, 음수: 감소)
 * @returns {string} CSS 클래스명 ("increase" 또는 "decrease")
 */
export function getComparisonClass(diff) {
    if (!diff) return "";
    return diff > 0 ? "increase" : "decrease";
}

/**
 * 비교 차이값에 따라 아이콘 클래스 반환
 * @param {number} diff - 비교 차이값 (양수: 증가, 음수: 감소)
 * @returns {string} FontAwesome 아이콘 클래스명
 */
export function getComparisonIcon(diff) {
    if (!diff) return "fas fa-minus";
    return diff > 0 ? "fas fa-arrow-up" : "fas fa-arrow-down";
}

/**
 * 계약 요금 구조 문자열 생성
 * @param {Object} contractData - 계약 데이터 객체
 * @returns {string} 요금 구조 문자열
 */
export function getContractFeeStructureParts(contractData) {
    const cost = contractData?.contractInfo?.cost;
    if (!cost) return [];

    const structures = [];
    if (cost.hasSeason === 1) structures.push("계절별");
    if (cost.hasTimeSlot === 1) structures.push("시간대별");
    if (cost.hasDayType === 1) structures.push("요일별");
    if (cost.hasProgressive === 1) structures.push("누진제");
    return structures.length > 0 ? structures : ["단일 요금"];
}

export function getContractFeeStructure(contractData) {
    const parts = getContractFeeStructureParts(contractData);
    return parts.length > 0 ? parts.join(", ") : "-";
}

/**
 * 계약 표시 텍스트 생성 (요금제명, 유효기간 포함)
 * @param {Object} contract - 계약 객체
 * @returns {string} 계약 표시 텍스트
 */
export function getContractDisplayText(contract) {
    if (!contract) return '';
    
    const contractName = contract.contractName || '';
    const costName = contract.costName || contract.cost?.costName || '';
    const startDate = contract.startDate ? formatDate(contract.startDate) : '';
    const endDate = contract.endDate ? formatDate(contract.endDate) : '';
    
    let displayText = contractName;
    
    // 요금제명 추가
    if (costName) {
        displayText += ` - ${costName}`;
    }
    
    // 유효기간 추가
    if (startDate || endDate) {
        const periodText = endDate ? `${startDate} ~ ${endDate}` : `${startDate} ~`;
        displayText += ` (${periodText})`;
    }
    
    return displayText;
}
